import React, { useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import ImageGallery from '../../components/ImageGallery/ImageGallery';
import EncryptedImage from '../../components/EncryptedImage/EncryptedImage';
import { useTranslation } from '../../hooks/useTranslation';
import {
  getCollections,
  getCollection,
  getCollectionPhotos,
  getSubCollections,
  getCover,
  getName,
  photoPath
} from '../../utils/collections';
import { encUrl } from '../../utils/encUrl';
import styles from './Portfolio.module.css';

const Portfolio = () => {
  const { category, sub } = useParams();
  const { t, language } = useTranslation();
  const collections = getCollections();
  const currentCategory = category || collections[0]?.slug;
  const collection = getCollection(currentCategory);
  const subCollections = useMemo(
    () => getSubCollections(currentCategory).map(s => ({ ...s, cover: getCover(currentCategory, s.slug) })),
    [currentCategory]
  );
  const images = useMemo(() => getCollectionPhotos(currentCategory, sub), [currentCategory, sub]);

  // Unknown collection → first one; unknown or empty sub-collection → whole collection
  if (!collection) return <Navigate to="/portfolio" replace />;
  if (sub && !subCollections.some(s => s.slug === sub)) {
    return <Navigate to={`/portfolio/${currentCategory}`} replace />;
  }

  const categories = collections.map(c => ({ name: getName(c, language), slug: c.slug }));

  // Trouver l'index de la catégorie actuelle
  const currentIndex = categories.findIndex(cat => cat.slug === currentCategory);
  const previousCategory = categories[currentIndex - 1];
  const nextCategory = categories[currentIndex + 1];

  const filterClass = (active) => `${styles.filter} ${active ? styles.filterActive : ''}`;

  return (
    <div className={styles.portfolio}>
      <div className={styles.navigation}>
        {previousCategory && (
          <Link
            to={`/portfolio/${previousCategory.slug}`}
            className={`${styles.navLink} ${styles.prevLink}`}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            {previousCategory.name}
          </Link>
        )}

        <h1 className={styles.title}>
          {categories[currentIndex].name}
        </h1>

        {nextCategory && (
          <Link
            to={`/portfolio/${nextCategory.slug}`}
            className={`${styles.navLink} ${styles.nextLink}`}
          >
            {nextCategory.name}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </Link>
        )}
      </div>

      {subCollections.length > 0 && (
        <nav className={styles.filters} aria-label={t('portfolio.filtersLabel')}>
          <Link
            to={`/portfolio/${currentCategory}`}
            className={filterClass(!sub)}
            aria-current={!sub ? 'page' : undefined}
          >
            {t('portfolio.all')}
          </Link>
          {subCollections.map(s => (
            <Link
              key={s.slug}
              to={`/portfolio/${currentCategory}/${s.slug}`}
              className={`${filterClass(sub === s.slug)} ${styles.filterWithThumb}`}
              aria-current={sub === s.slug ? 'page' : undefined}
            >
              {/* Cover chosen in the Studio (else first photo); decorative, the name labels the link */}
              <span className={styles.filterThumb} aria-hidden="true">
                <EncryptedImage
                  src={encUrl(photoPath(s.cover))}
                  alt=""
                  className={styles.filterThumbImage}
                  cover
                />
              </span>
              {getName(s, language)}
            </Link>
          ))}
        </nav>
      )}

      {/* Re-keyed per filter so the gallery fades in and the lightbox resets */}
      <div key={sub || ''} className="page-enter">
        <ImageGallery images={images} />
      </div>
    </div>
  );
};

export default Portfolio;
